import { useState } from "react";
import { createRoot } from "react-dom/client";
import api from "@/lib/api";
import { FileWarning } from "lucide-react";

function Dialog({ nome, onPick }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" data-testid="conflitto-nome-modal">
      <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-md">
        <div className="flex items-center gap-2 font-display font-bold mb-2"><FileWarning size={18} className="text-[#FC82ED]" /> File già presente</div>
        <p className="text-sm text-slate-600 mb-4 break-words">Esiste già <b>{nome}</b> in questa cartella. Cosa vuoi fare?</p>
        <div className="flex flex-wrap gap-2 justify-end">
          <button data-testid="conflitto-annulla" onClick={() => onPick(null)} className="px-3 py-2 rounded-xl border border-[var(--border)] text-sm font-semibold">Annulla</button>
          <button data-testid="conflitto-entrambi" onClick={() => onPick("entrambi")} className="px-3 py-2 rounded-xl border border-[#111111] text-sm font-semibold">Tieni entrambi</button>
          <button data-testid="conflitto-sostituisci" onClick={() => onPick("sostituisci")} className="px-3 py-2 rounded-xl bg-[#111111] text-white text-sm font-semibold">Sostituisci</button>
        </div>
      </div>
    </div>
  );
}

export function chiediConflitto(nome) {
  return new Promise((resolve) => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    const root = createRoot(el);
    const onPick = (v) => { root.unmount(); el.remove(); resolve(v); };
    root.render(<Dialog nome={nome} onPick={onPick} />);
  });
}

function DialogNome({ nome, onPick, titolo = "Nome documento", bottone = "Carica", noExt = false }) {
  const [v, setV] = useState(nome);
  const ext = !noExt && nome.includes(".") ? nome.slice(nome.lastIndexOf(".")) : "";
  const ok = () => { const t = v.trim(); if (!t) return; onPick(ext && !t.toLowerCase().endsWith(ext.toLowerCase()) ? t + ext : t); };
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" data-testid="nome-documento-dialog">
      <div className="w-full max-w-md bg-white rounded-2xl p-5 shadow-2xl">
        <label className="block text-xs font-semibold text-[var(--muted)] mb-1">{titolo}</label>
        <input autoFocus data-testid="nome-documento-input" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ok(); if (e.key === "Escape") onPick(null); }}
               className="w-full px-3 py-2.5 rounded-lg border border-[var(--border)] text-base sm:text-sm focus:border-[#FC82ED] outline-none" />
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="nome-documento-annulla" onClick={() => onPick(null)} className="px-4 py-2 rounded-lg border border-[var(--border)] text-sm">Annulla</button>
          <button data-testid="nome-documento-ok" onClick={ok} className="px-4 py-2 rounded-lg bg-[#111111] text-white text-sm font-semibold">{bottone}</button>
        </div>
      </div>
    </div>
  );
}

let coda = Promise.resolve();
export function chiediNome(nome, opz = {}) {
  const p = coda.then(() => new Promise((resolve) => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    const root = createRoot(el);
    root.render(<DialogNome nome={nome} {...opz} onPick={(x) => { root.unmount(); el.remove(); resolve(x); }} />);
  }));
  coda = p.catch(() => {});
  return p;
}

export const chiediRinomina = (nome) => chiediNome(nome || "", { bottone: "Salva" });

// Nessuna finestra di nome al caricamento: i file tengono il nome originale (modificabile dopo).
export async function nominaFiles(list) {
  return Array.from(list || []);
}

export function rinominaFile(f, n) {
  const t = (n || "").trim();
  if (!t || t === f.name) return f;
  const ext = f.name.includes(".") ? f.name.slice(f.name.lastIndexOf(".")) : "";
  return new File([f], ext && !t.toLowerCase().endsWith(ext.toLowerCase()) ? t + ext : t, { type: f.type, lastModified: f.lastModified });
}

export function NomeDocumentoInline({ valore, onSalva, testid = "nome-documento-inline" }) {
  const [v, setV] = useState(valore);
  const salva = () => { const t = v.trim(); if (t && t !== valore) onSalva(t); };
  return (
    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--muted)] w-full" onClick={(e) => e.stopPropagation()}>
      Nome documento
      <input data-testid={testid} value={v} onChange={(e) => setV(e.target.value)} onBlur={salva} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className="flex-1 min-w-0 px-2 py-1 rounded-md border border-[#FC82ED]/50 bg-white text-xs font-normal text-[#111111] focus:border-[#FC82ED] outline-none" />
    </label>
  );
}

export async function msgErroreFile(e) {
  let d = e?.response?.data;
  if (d instanceof Blob) { try { d = JSON.parse(await d.text()); } catch { d = null; } }
  const s = e?.response?.status;
  const det = typeof d?.detail === "string" ? d.detail : "";
  if (s === 403) return "Non hai il permesso di aprire questo file." + (det && det !== "Accesso negato" ? ` (${det})` : "");
  if (s === 404) return "File non trovato: potrebbe essere stato spostato o eliminato.";
  return det || "Impossibile aprire il file. Controlla la connessione e riprova.";
}

function DialogErrore({ msg, onClose }) {
  return (
    <div className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center p-4" data-testid="errore-file-dialog" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 font-display font-bold mb-2"><FileWarning size={18} className="text-red-500" /> File non disponibile</div>
        <p className="text-sm text-slate-600 mb-4" data-testid="errore-file-msg">{msg}</p>
        <div className="flex justify-end"><button data-testid="errore-file-chiudi" onClick={onClose} className="px-4 py-2 rounded-lg bg-[#111111] text-white text-sm font-semibold">Chiudi</button></div>
      </div>
    </div>
  );
}

export async function mostraErroreFile(e) {
  const msg = typeof e === "string" ? e : await msgErroreFile(e);
  const el = document.createElement("div");
  document.body.appendChild(el);
  const root = createRoot(el);
  root.render(<DialogErrore msg={msg} onClose={() => { root.unmount(); el.remove(); }} />);
}

export function ErroreFileBox({ msg, onClose, dark = false }) {
  return (
    <div className={`text-center p-6 max-w-sm ${dark ? "text-white" : "text-slate-700"}`} data-testid="errore-file-box" onClick={(e) => e.stopPropagation()}>
      <FileWarning size={36} className="mx-auto mb-3 text-red-400" />
      <p className="text-sm mb-4" data-testid="errore-file-msg">{msg}</p>
      <button data-testid="errore-file-chiudi" onClick={onClose} className="px-4 py-2 rounded-lg bg-white text-[#111111] border border-[var(--border)] text-sm font-semibold">Chiudi</button>
    </div>
  );
}

// File oltre 8 MB: prima arriva a pezzi da 5 MB (con ripresa), poi si registra con caricamento_id.
export async function postGrande(url, fd, config = {}) {
  const f = fd.get("file");
  if (!(f instanceof Blob) || f.size <= 8 * 1024 * 1024) return api.post(url, fd, config);
  const { caricaGrande } = await import("@/lib/codaCaricamenti");
  const g = await caricaGrande(f, url);
  fd.delete("file"); fd.set("caricamento_id", g.cid);
  try { const r = await api.post(url, fd, { ...config, timeout: 300000 }); g.fine(true); return r; }
  catch (e) { if (e.response?.status !== 409) g.fine(false, e.response?.data?.detail); else g.fine(true); throw e; }
}

export async function postUpload(fd, config = {}) {
  const cfg = { headers: { "Content-Type": "multipart/form-data" }, ...config };
  try {
    return (await postGrande("/files/upload", fd, cfg)).data;
  } catch (e) {
    const d = e.response?.data?.detail;
    if (e.response?.status !== 409 || d?.code !== "nome_duplicato") throw e;
    const scelta = await chiediConflitto(d.nome);
    if (!scelta) throw Object.assign(new Error("Caricamento annullato"), { response: { data: { detail: "Caricamento annullato" } }, annullato: true });
    fd.set("conflitto", scelta);
    return (await api.post("/files/upload", fd, cfg)).data;
  }
}
